import { FC } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Typography, Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import greyDialogTitleSx from '../greyDialogTitleSx';

// Shape returned by /api/v1/gst-lookup/search as data.summary for mode 'gstin'
export interface GstinSummary {
  gstin: string;
  pan: string | null;
  legalName: string | null;
  tradeName: string | null;
  status: string | null;
  registrationDate: string | null;
  cancellationDate: string | null;
  constitution: string | null;
  taxpayerType: string | null;
  natureOfBusiness: string[];
  einvoiceStatus: string | null;
  stateCode: string | null;
  stateName: string | null;
  address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    district: string | null;
    state: string | null;
    pincode: string | null;
    full: string | null;
  };
}

interface Props {
  open: boolean;
  summary: GstinSummary | null;
  onClose: () => void;
  // "Fill form": the parent copies the portal details into its own fields.
  onUse: (summary: GstinSummary) => void;
}

// Matches the existing party/account form buttons (blue outline on white).
export const appBtnSx = { color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', boxShadow: 'none', textTransform: 'none', '&:hover': { color: '#488dff', backgroundColor: '#f0f6ff', border: '1px solid #488dff', boxShadow: 'none' } };
// Flat status badge, same palette the lists use.
const statusBadge = (active: boolean) => ({ backgroundColor: active ? '#e8f5e9' : '#ffebee', color: active ? '#2e7d32' : '#c62828', fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 12 });

// What the GST portal returned for a GSTIN, with a button to fill the form from it.
// The captcha step happens inline next to the GST Number field (useGstinVerify).
const GstinVerifyDialog: FC<Props> = ({ open, summary, onClose, onUse }) => {
  const row = (label: string, value?: string | null) => value ? (
    <Box sx={{ display: 'flex', py: 0.4 }}>
      <Typography variant='body2' sx={{ width: 150, color: '#5f6368', flexShrink: 0 }}>{label}</Typography>
      <Typography variant='body2' sx={{ color: '#1a1a1a', wordBreak: 'break-word' }}>{value}</Typography>
    </Box>
  ) : null;

  return (
    <Dialog open={open && !!summary} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ ...greyDialogTitleSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant='h5'>GSTIN Verified</Typography>
          <Typography variant='body2'>{summary?.gstin}</Typography>
        </Box>
        <IconButton size='small' onClick={onClose}><CloseIcon fontSize='small' /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {summary && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant='h6' sx={{ fontSize: 16, fontWeight: 600 }}>{summary.tradeName || summary.legalName || summary.gstin}</Typography>
              {summary.status && <span style={statusBadge(summary.status.toLowerCase() === 'active')}>{summary.status}</span>}
            </Box>
            <Divider sx={{ mb: 1 }} />
            {/* GST "legal name" is the PAN holder (owner, for proprietorships); trade name is the business. */}
            {row('Legal/Business name', summary.tradeName || summary.legalName)}
            {summary.tradeName && summary.tradeName !== summary.legalName && row('Owner name', summary.legalName)}
            {row('PAN', summary.pan)}
            {row('Constitution', summary.constitution)}
            {row('Taxpayer type', summary.taxpayerType)}
            {row('Registered on', summary.registrationDate)}
            {row('Cancelled on', summary.cancellationDate)}
            {row('State', summary.stateName ? `${summary.stateName} (${summary.stateCode})` : null)}
            {row('Address', summary.address?.full)}
            {row('Nature of business', summary.natureOfBusiness?.length ? summary.natureOfBusiness.join(', ') : null)}
            {row('e-Invoice', summary.einvoiceStatus)}
            {summary.status && summary.status.toLowerCase() !== 'active' && (
              <Typography variant='body2' color='error' sx={{ mt: 1 }}>This GSTIN is not active. Fill the form only if you are sure.</Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={() => summary && onUse(summary)}>Use Details</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GstinVerifyDialog;
